import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { translateError } from '../i18n.js';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import { clearMeetingsMenuCache } from '../components/layout/Sidebar.jsx';
import { useGoalsFor, GoalChips } from '../components/shared/GoalChips.jsx';

// ── Helpers (shared with MeetingDetailView) ──────────────────────────────────

export const ROLE_LABELS = { voorzitter: 'Voorzitter', secretaris: 'Secretaris', lid: 'Lid', adviseur: 'Adviseur' };
const ROLE_ORDER = ['voorzitter', 'secretaris', 'lid', 'adviseur'];

// Meetings store local "YYYY-MM-DDTHH:mm". Why built from parts: Safari parses
// some ISO-like strings as UTC or not at all.
export function parseLocal(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || '');
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) : null;
}

export function formatWhen(startsAt, endsAt) {
  const s = parseLocal(startsAt);
  if (!s) return '—';
  const e = parseLocal(endsAt);
  const date = s.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const time = d => d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  return `${date}, ${time(s)}${e ? `–${time(e)}` : ''}`;
}

function toLocalInput(d) {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function RoleBadge({ role }) {
  if (!role) return null;
  const strong = role === 'voorzitter' || role === 'secretaris';
  return (
    <span style={`font-size:11px;padding:2px 8px;border-radius:10px;white-space:nowrap;${strong ? 'background:var(--color-primary);color:#fff' : 'background:var(--color-border);color:var(--color-text-muted)'}`}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

export const STATUS_LABELS = { gepland: 'Gepland', gehouden: 'Gehouden', geannuleerd: 'Geannuleerd' };

// ── Body form (create / edit gremium) ────────────────────────────────────────

function BodyModal({ body, onClose, onSaved }) {
  const complex = useAuth().isComplex;
  const isEdit = !!body?.id;
  const [options, setOptions] = useState(null);
  const [name, setName] = useState(body?.name || '');
  const [description, setDescription] = useState(body?.description || '');
  const originalCommission = body?.commission_id ? String(body.commission_id) : '';
  const [commissionId, setCommissionId] = useState(originalCommission);
  // Stored rows: extra people and role overrides for commission members
  const [settings, setSettings] = useState(body?.member_settings || []);
  const [addUserId, setAddUserId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api('/api/meetings/options').then(setOptions).catch(e => setError(translateError(e.code, e.detail)));
  }, []);

  const userName = id => options?.users.find(u => u.id === id)?.display_name || `#${id}`;
  // Commission members of an existing body, so their role can be set too
  const commissionMembers = isEdit && String(body.commission_id || '') === commissionId
    ? (body.members || []).filter(m => m.source === 'commissie')
    : [];
  const roleOf = id => settings.find(s => s.user_id === id)?.role;
  const setRole = (id, role) => setSettings(prev => {
    const rest = prev.filter(s => s.user_id !== id);
    return role ? [...rest, { user_id: id, role }] : rest;
  });
  const extras = settings.filter(s => !commissionMembers.some(m => m.user_id === s.user_id));

  // Why: a role set for a member of the old commission must not survive as an
  // "extra person" (with that role) when the body moves to another commission.
  const changeCommission = (value) => {
    setCommissionId(value);
    const oldCommissionIds = new Set((body?.members || []).filter(m => m.source === 'commissie').map(m => m.user_id));
    if (value !== originalCommission) {
      setSettings((body?.member_settings || []).filter(s => !oldCommissionIds.has(s.user_id)));
    } else {
      setSettings(body?.member_settings || []);
    }
  };

  const addPerson = () => {
    const id = Number(addUserId);
    if (!id || settings.some(s => s.user_id === id)) return;
    setSettings(prev => [...prev, { user_id: id, role: 'lid' }]);
    setAddUserId('');
  };

  const save = async () => {
    if (!name.trim()) { setError('Naam is verplicht'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = { name: name.trim(), description, commission_id: commissionId ? Number(commissionId) : null, members: settings };
      const saved = isEdit
        ? await api(`/api/meetings/bodies/${body.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : await api('/api/meetings/bodies', { method: 'POST', body: JSON.stringify(payload) });
      onSaved(saved);
    } catch (e) {
      setError(translateError(e.code, e.detail));
      setSaving(false);
    }
  };

  const labelStyle = 'display:block;font-size:13px;font-weight:600;margin-bottom:6px';
  const hint = 'font-size:11px;color:var(--color-text-muted);margin-top:4px';

  return (
    <Modal isOpen title={isEdit ? 'Gremium bewerken' : 'Nieuw gremium'} onClose={onClose}>
      {error && <div class="alert alert-error mb-4">{error}</div>}
      {!options && !error && <div style="font-size:13px;color:var(--color-text-muted)">Laden…</div>}
      {options && (
        <>
          <div style="margin-bottom:14px">
            <label style={labelStyle}>Naam *</label>
            <input class="form-input" value={name} onInput={e => setName(e.target.value)} placeholder="bv. Sectiebestuur Voetbal" maxLength={80} />
          </div>
          <div style="margin-bottom:14px">
            <label style={labelStyle}>Omschrijving</label>
            <input class="form-input" value={description} onInput={e => setDescription(e.target.value)} placeholder="Optioneel" maxLength={200} />
          </div>
          {/* A sportcomplex has no commissions: the besturen of its clubs are members */}
          {complex ? (
            <div style={`${hint};margin-bottom:14px`}>De besturen van alle aangesloten clubs (clubbeheerders en contactpersonen) zijn automatisch lid. Voeg hieronder de medewerkers van het complex toe, bijvoorbeeld als voorzitter of secretaris.</div>
          ) : <div style="margin-bottom:14px">
            <label style={labelStyle}>Commissie</label>
            <select class="form-input" value={commissionId} onChange={e => changeCommission(e.target.value)}>
              <option value="">— Geen, alleen losse personen —</option>
              {options.commissions.map(c => <option key={c.id} value={String(c.id)}>{c.section_name} · {c.name}</option>)}
            </select>
            <div style={hint}>
              Iedereen met een categorie in deze commissie is automatisch lid.
              {commissionId && commissionId !== originalCommission && ' Rollen van commissieleden stel je in na het opslaan.'}
            </div>
          </div>}

          {commissionMembers.length > 0 && (
            <div style="margin-bottom:14px">
              <label style={labelStyle}>Rollen van commissieleden</label>
              <div style="border:1px solid var(--color-border);border-radius:8px;max-height:180px;overflow-y:auto">
                {commissionMembers.map(m => (
                  <div key={m.user_id} style="display:flex;align-items:center;gap:8px;padding:6px 10px;border-bottom:1px solid var(--color-border);font-size:13px">
                    <span style="flex:1">{m.display_name}</span>
                    <select class="form-input" style="width:auto;height:30px;font-size:12px" value={roleOf(m.user_id) || 'lid'}
                      onChange={e => setRole(m.user_id, e.target.value === 'lid' ? null : e.target.value)}>
                      {ROLE_ORDER.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style="margin-bottom:20px">
            <label style={labelStyle}>{commissionId ? 'Extra personen' : 'Leden'}</label>
            {extras.length > 0 && (
              <div style="border:1px solid var(--color-border);border-radius:8px;margin-bottom:8px">
                {extras.map(s => (
                  <div key={s.user_id} style="display:flex;align-items:center;gap:8px;padding:6px 10px;border-bottom:1px solid var(--color-border);font-size:13px">
                    <span style="flex:1">{userName(s.user_id)}</span>
                    <select class="form-input" style="width:auto;height:30px;font-size:12px" value={s.role} onChange={e => setRole(s.user_id, e.target.value)}>
                      {ROLE_ORDER.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                    <button type="button" onClick={() => setRole(s.user_id, null)} title="Verwijderen"
                      style="border:none;background:none;color:var(--color-text-muted);cursor:pointer;font-size:14px">×</button>
                  </div>
                ))}
              </div>
            )}
            <div style="display:flex;gap:8px">
              <select class="form-input" value={addUserId} onChange={e => setAddUserId(e.target.value)}>
                <option value="">Persoon kiezen…</option>
                {options.users.filter(u => !settings.some(s => s.user_id === u.id) && !commissionMembers.some(m => m.user_id === u.id))
                  .map(u => <option key={u.id} value={String(u.id)}>{u.display_name}</option>)}
              </select>
              <Button variant="secondary" size="sm" onClick={addPerson} disabled={!addUserId}>Toevoegen</Button>
            </div>
            <div style={hint}>Bijvoorbeeld een adviseur of notulist die niet in de commissie zit.</div>
          </div>

          <div class="flex gap-2 justify-end">
            <Button variant="secondary" onClick={onClose} disabled={saving}>Annuleer</Button>
            <Button variant="primary" onClick={save} disabled={saving}>{saving ? '...' : 'Opslaan'}</Button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── Plan meeting ─────────────────────────────────────────────────────────────

export function MeetingFormModal({ meeting, title: modalTitle, onClose, onSubmit }) {
  const start = new Date();
  start.setDate(start.getDate() + 7);
  start.setHours(20, 0, 0, 0);
  const end = new Date(start.getTime() + 2 * 3600000);
  const [title, setTitle] = useState(meeting?.title || '');
  const [startsAt, setStartsAt] = useState(meeting?.starts_at || toLocalInput(start));
  const [endsAt, setEndsAt] = useState(meeting?.ends_at || toLocalInput(end));
  const [location, setLocation] = useState(meeting?.location || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Keep the duration when the start moves
  const changeStart = (v) => {
    const oldS = parseLocal(startsAt), oldE = parseLocal(endsAt), s = parseLocal(v);
    setStartsAt(v);
    if (oldS && oldE && s) setEndsAt(toLocalInput(new Date(s.getTime() + (oldE - oldS))));
  };

  const submit = async () => {
    if (!title.trim()) { setError('Titel is verplicht'); return; }
    if (!(endsAt > startsAt)) { setError('Het einde moet na het begin liggen'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSubmit({ title: title.trim(), starts_at: startsAt, ends_at: endsAt, location });
    } catch (e) {
      setError(translateError(e.code, e.detail));
      setSaving(false);
    }
  };

  const labelStyle = 'display:block;font-size:13px;font-weight:600;margin-bottom:6px';
  return (
    <Modal isOpen title={modalTitle} onClose={onClose}>
      {error && <div class="alert alert-error mb-4">{error}</div>}
      <div style="margin-bottom:14px">
        <label style={labelStyle}>Titel *</label>
        <input class="form-input" value={title} onInput={e => setTitle(e.target.value)} placeholder="bv. Bestuursvergadering oktober" maxLength={120} />
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px">
        <div>
          <label style={labelStyle}>Begin *</label>
          <input class="form-input" type="datetime-local" value={startsAt} onInput={e => changeStart(e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Einde *</label>
          <input class="form-input" type="datetime-local" value={endsAt} onInput={e => setEndsAt(e.target.value)} />
        </div>
      </div>
      <div style="margin-bottom:20px">
        <label style={labelStyle}>Locatie</label>
        <input class="form-input" value={location} onInput={e => setLocation(e.target.value)} placeholder="bv. Bestuurskamer of Teams-link" maxLength={200} />
      </div>
      {!meeting && (
        <div style="font-size:12px;color:var(--color-text-muted);margin-bottom:16px">
          De vergadering komt in de kalender van alle leden van het gremium, met aanmelden. De agenda start met de vaste punten.
        </div>
      )}
      <div class="flex gap-2 justify-end">
        <Button variant="secondary" onClick={onClose} disabled={saving}>Annuleer</Button>
        <Button variant="primary" onClick={submit} disabled={saving}>{saving ? '...' : meeting ? 'Opslaan' : 'Plannen'}</Button>
      </div>
    </Modal>
  );
}

// ── Overview: my bodies ──────────────────────────────────────────────────────

export default function MeetingsView() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api('/api/meetings/bodies').then(setData).catch(e => setError(translateError(e.code, e.detail)));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">🗓️ Vergaderingen</h1>
        {data?.can_create && <Button variant="primary" size="sm" onClick={() => setCreating(true)}>+ Nieuw gremium</Button>}
      </div>
      {error && <div class="alert alert-error mb-4">{error}</div>}
      {!data && !error && <div style="color:var(--color-text-muted)">Laden…</div>}
      {data && data.bodies.length === 0 && (
        <Card>
          <div style="text-align:center;padding:24px 12px;color:var(--color-text-muted)">
            <div style="font-size:28px;margin-bottom:8px">🗓️</div>
            <div style="font-weight:600;color:var(--color-text);margin-bottom:4px">Je zit nog in geen vergadergremium</div>
            <div style="font-size:13px">
              {data.can_create ? 'Maak een gremium aan, bijvoorbeeld voor een sectiebestuur of commissie.' : 'Zodra je lid bent van een bestuur of commissie die hier vergadert, zie je die hier.'}
            </div>
          </div>
        </Card>
      )}
      {data && data.bodies.length > 0 && (
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:12px">
          {data.bodies.map(b => (
            <button key={b.id} onClick={() => route(`/meetings/bodies/${b.id}`)}
              style="text-align:left;border:1px solid var(--color-border);border-radius:12px;background:var(--color-surface);padding:16px;cursor:pointer;color:var(--color-text)">
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
                <span style="font-size:15px;font-weight:700;flex:1">{b.name}</span>
                {b.archived ? <span style="font-size:11px;color:var(--color-text-muted)">Gearchiveerd</span> : <RoleBadge role={b.my_role} />}
              </div>
              <div style="font-size:12px;color:var(--color-text-muted);margin-bottom:10px">
                {b.commission_name ? `Commissie ${b.commission_name} · ` : ''}{b.member_count} {b.member_count === 1 ? 'lid' : 'leden'}
              </div>
              <div style="font-size:12px">
                {b.next_meeting
                  ? <>📅 <strong>{b.next_meeting.title}</strong><br /><span style="color:var(--color-text-muted)">{formatWhen(b.next_meeting.starts_at)}</span></>
                  : <span style="color:var(--color-text-muted)">Geen vergadering gepland</span>}
              </div>
            </button>
          ))}
        </div>
      )}
      {creating && <BodyModal onClose={() => setCreating(false)} onSaved={(b) => { setCreating(false); clearMeetingsMenuCache(); route(`/meetings/bodies/${b.id}`); }} />}
    </div>
  );
}

// ── Decision list (per body, and club-wide on its own page) ──────────────────

export function DecisionList({ decisions, showBody }) {
  const goalMap = useGoalsFor('decision', (decisions || []).map(d => d.id));
  if (!decisions) return <div style="font-size:13px;color:var(--color-text-muted)">Laden…</div>;
  if (decisions.length === 0) return <div style="font-size:13px;color:var(--color-text-muted);padding:8px 0">Geen besluiten gevonden.</div>;
  return decisions.map(d => (
    <div key={d.id} style="display:flex;gap:10px;padding:10px 0;border-bottom:1px solid var(--color-border);align-items:flex-start">
      <span style="font-size:11px;font-weight:700;padding:2px 7px;border-radius:6px;background:var(--color-primary);color:#fff;white-space:nowrap">{d.number}</span>
      <div style="flex:1;min-width:0">
        <div style="font-size:14px;white-space:pre-wrap">{d.text}</div>
        <div style="font-size:12px;color:var(--color-text-muted);margin-top:2px">
          {showBody ? `${d.body_name} · ` : ''}
          {/* Why: the meeting itself is for members only; others just see its name */}
          {d.can_open === false
            ? d.meeting_title
            : <a href={`/meetings/${d.meeting_id}`} onClick={e => { e.preventDefault(); route(`/meetings/${d.meeting_id}`); }} style="color:inherit">{d.meeting_title}</a>}
          {` · ${formatWhen(d.meeting_starts_at).split(',')[0]}`}
          {!showBody && d.visibility === 'club' ? ' · Clubbreed' : ''}
        </div>
        <GoalChips goals={goalMap[d.id]} />
      </div>
    </div>
  ));
}

function useDecisionSearch(url) {
  const [q, setQ] = useState('');
  const [decisions, setDecisions] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let stale = false; // Why: a slower, older search must not overwrite a newer one
    const timer = setTimeout(() => {
      api(`${url}${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`)
        .then(d => { if (!stale) { setDecisions(d); setError(null); } })
        .catch(e => { if (!stale) setError(translateError(e.code, e.detail)); });
    }, 200);
    return () => { stale = true; clearTimeout(timer); };
  }, [url, q]);
  return { q, setQ, decisions, error };
}

function BodyDecisions({ bodyId }) {
  const { q, setQ, decisions, error } = useDecisionSearch(`/api/meetings/bodies/${bodyId}/decisions`);
  return (
    <Card title="Besluitenlijst">
      <input class="form-input" value={q} onInput={e => setQ(e.target.value)} placeholder="Zoek op tekst of nummer…" style="margin-bottom:6px;height:34px;font-size:13px" aria-label="Zoek in besluiten" />
      {error ? <div class="alert alert-error">{error}</div> : <DecisionList decisions={decisions} />}
    </Card>
  );
}

// Club-wide page: every decision a body marked "clubbreed". For every club member.
export function DecisionsView() {
  const { q, setQ, decisions, error } = useDecisionSearch('/api/meetings/decisions');
  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">⚖️ Besluiten</h1>
      </div>
      <Card>
        <div style="font-size:13px;color:var(--color-text-muted);margin-bottom:10px">
          Besluiten die besturen en commissies voor de hele club zichtbaar hebben gemaakt.
        </div>
        <input class="form-input" value={q} onInput={e => setQ(e.target.value)} placeholder="Zoek op tekst of nummer…" style="margin-bottom:6px;height:34px;font-size:13px" aria-label="Zoek in besluiten" />
        {error ? <div class="alert alert-error">{error}</div> : <DecisionList decisions={decisions} showBody />}
      </Card>
    </div>
  );
}

// ── One body: members and meetings ───────────────────────────────────────────

export function MeetingBodyView({ id }) {
  const auth = useAuth();
  const [body, setBody] = useState(null);
  const [error, setError] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [editing, setEditing] = useState(false);
  const [planning, setPlanning] = useState(false);

  const load = () => api(`/api/meetings/bodies/${id}`).then(setBody).catch(e => setError(translateError(e.code, e.detail)));
  useEffect(() => { load(); }, [id]);

  const archive = async () => {
    try {
      await api(`/api/meetings/bodies/${id}`, { method: 'DELETE' });
      setConfirmArchive(false);
      clearMeetingsMenuCache();
      load();
    } catch (e) { setError(translateError(e.code, e.detail)); }
  };
  const restore = async () => {
    try { await api(`/api/meetings/bodies/${id}/restore`, { method: 'POST' }); load(); }
    catch (e) { setError(translateError(e.code, e.detail)); }
  };

  const plan = async (form) => {
    const m = await api(`/api/meetings/bodies/${id}/meetings`, { method: 'POST', body: JSON.stringify(form) });
    route(`/meetings/${m.id}`);
  };

  const MeetingRow = ({ m }) => (
    <button onClick={() => route(`/meetings/${m.id}`)}
      style="display:flex;align-items:center;gap:12px;width:100%;text-align:left;padding:12px 4px;border:none;border-bottom:1px solid var(--color-border);background:transparent;cursor:pointer;color:var(--color-text)">
      <div style="flex:1;min-width:0">
        <div style={`font-size:14px;font-weight:600;${m.status === 'geannuleerd' ? 'text-decoration:line-through;color:var(--color-text-muted)' : ''}`}>{m.title}</div>
        <div style="font-size:12px;color:var(--color-text-muted)">{formatWhen(m.starts_at, m.ends_at)}{m.location ? ` · ${m.location}` : ''}</div>
      </div>
      {m.status !== 'gepland' && <span style="font-size:11px;color:var(--color-text-muted)">{STATUS_LABELS[m.status]}</span>}
      <span style="color:var(--color-text-muted)">›</span>
    </button>
  );

  if (error) return <div><a href="/meetings" onClick={e => { e.preventDefault(); route('/meetings'); }} style="font-size:13px">‹ Vergaderingen</a><div class="alert alert-error mt-4">{error}</div></div>;
  if (!body) return <div style="color:var(--color-text-muted)">Laden…</div>;

  return (
    <div>
      <a href="/meetings" onClick={e => { e.preventDefault(); route('/meetings'); }} style="font-size:13px;color:var(--color-text-muted);text-decoration:none">‹ Vergaderingen</a>
      <div class="page-header" style="margin-top:6px">
        <div>
          <h1 class="page-title">{body.name}</h1>
          <div style="font-size:13px;color:var(--color-text-muted)">
            {auth.isComplex ? 'Gebruikersoverleg met de besturen van de clubs' : body.commission_name ? `Commissie ${body.commission_name}` : 'Losse samenstelling'}{body.description ? ` · ${body.description}` : ''}
          </div>
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap">
          {body.archived ? (
            body.can_manage && <Button variant="secondary" size="sm" onClick={restore}>Terughalen</Button>
          ) : (
            <>
              {body.can_manage && <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>Gremium bewerken</Button>}
              {(body.can_archive ?? auth.hasPermission('meetings', 'delete')) && <Button variant="secondary" size="sm" onClick={() => setConfirmArchive(true)}>Archiveren</Button>}
              {body.can_edit && <Button variant="primary" size="sm" onClick={() => setPlanning(true)}>+ Vergadering plannen</Button>}
            </>
          )}
        </div>
      </div>

      {body.archived ? (
        <div class="alert mb-4" style="background:var(--color-border);border-radius:8px;padding:10px 12px;font-size:13px">
          Dit gremium is gearchiveerd. Eerdere vergaderingen en notulen zijn nog te lezen; plannen kan niet meer.
        </div>
      ) : null}
      <div style="display:grid;grid-template-columns:minmax(0,2fr) minmax(0,1fr);gap:16px;align-items:start" class="meetings-grid">
        <div>
          <Card title="Komende vergaderingen">
            {body.upcoming.length === 0
              ? <div style="font-size:13px;color:var(--color-text-muted);padding:8px 0">Nog niets gepland.</div>
              : body.upcoming.map(m => <MeetingRow key={m.id} m={m} />)}
          </Card>
          {body.past.length > 0 && (
            <div style="margin-top:16px">
              <Card title="Eerdere vergaderingen">
                {body.past.map(m => <MeetingRow key={m.id} m={m} />)}
              </Card>
            </div>
          )}
        </div>
        <div>
        <Card title={`Leden (${body.members.length})`}>
          {body.members.length === 0 && <div style="font-size:13px;color:var(--color-text-muted)">Nog geen leden.</div>}
          {body.members.map(m => (
            <div key={m.user_id} style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--color-border);font-size:13px">
              <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{m.display_name}</span>
              <RoleBadge role={m.role} />
            </div>
          ))}
        </Card>
        </div>
      </div>
      <div style="margin-top:16px">
        <BodyDecisions bodyId={id} />
      </div>

      {confirmArchive && (
        <Modal isOpen title="Gremium archiveren?" onClose={() => setConfirmArchive(false)}>
          <p style="font-size:14px;margin:0 0 16px">Komende vergaderingen worden geannuleerd en verdwijnen uit de kalender. Eerdere vergaderingen en notulen blijven bewaard; je kunt het gremium later terughalen.</p>
          <div class="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setConfirmArchive(false)}>Terug</Button>
            <Button variant="danger" onClick={archive}>Archiveren</Button>
          </div>
        </Modal>
      )}
      {editing && <BodyModal body={body} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
      {planning && <MeetingFormModal title="Vergadering plannen" onClose={() => setPlanning(false)} onSubmit={plan} />}
    </div>
  );
}
